import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected, PayloadAction } from "@reduxjs/toolkit"
import { UserPreferenceRequest, UserPreferenceResponse } from "../../services/endpoints/userPreference"
import * as Services from '../../services/endpoints'
import { formatError } from "../lib/errors"
import { NetworkRequestError } from "../../services/lib"
import { _getFromLocalStorageItem, _getJsonFromUser, _getSessionFromJson, _saveOrDeleteLocalStorageItem, flushToLocalStorage, getFromLocalStorage, LocalStorageUser, SessionData } from "../lib/localStorage"
import { sessionSlice } from "./sessionSlice"

//todo impliment local storage here
//import { LocalStorageUser } from "../lib/localStorage"
const sliceName = 'UserPreference'

export interface UserPreferenceStateItem {
  id: number,
  name: string,
  val: string,
  loading: boolean,
  error: string | null
}
export interface UserPreferenceStateData {
  [key: string]:  UserPreferenceStateItem | undefined
}
export interface UserPreferenceState {
  data: UserPreferenceStateData
  userId: number | null
  loading: boolean
  error: NetworkRequestError | null
}



const _formatter = (response: {name: string, val: string, id: number}): UserPreferenceStateItem  => {
  return {
    name: response.name,
    val: response.val,
    loading: false,
    error: null,
    id: response.id
  }
}

const _initializeState = (): UserPreferenceState => {
  const data = getFromLocalStorage()
  const [userId, prefs] = _formatFromUserSession(data?.user)

  return {
    data: prefs,
    loading: false,
    error: null,
    userId: userId
  }
}

const _formatFromUserSession = (data: LocalStorageUser | null): [number | null, UserPreferenceStateData] => {
  if(data === null) {
    return [null, {}]
  }

  const prefs = Object.entries(data.preferences)
  .reduce<UserPreferenceStateData>((a, [name, i]) => {
    a[name] = _formatter({id: i.id, val: `${i.val}`, name})
    return a
  }, {})

  return [parseInt(data.id), prefs]
}

export const updateUserPreference = createAsyncThunk(
  `${sliceName}/updateUserPreferecne`,
  async (body: UserPreferenceRequest, { dispatch, rejectWithValue }) => {
    try {
      if(body.id) {
         const data = _formatter(await Services.updateUserPreference(body.id, body.val))
         dispatch(sessionSlice.actions.updatePreferences({
           name: data.name,
           id: data.id,
           val: data.val
         }))
         return data
      } else {
         const data = _formatter(await Services.createUserPreference(body.name, body.val))
         dispatch(sessionSlice.actions.updatePreferences({
           name: data.name,
           id: data.id,
           val: data.val
         }))
         return data
        }
    } catch (err) {
      return rejectWithValue(err)
    }
  }
)

export const getAllUserPreference = createAsyncThunk(
  `${sliceName}/getAllUserPreference`,
  async (_, { rejectWithValue }) => {
    try {
      const res = await Services.getAllUserPreference()
      const data = res.reduce<UserPreferenceStateData>((a, i) => {
        a[i.name] = _formatter(i)
        return a
      },{})
      return data
    } catch (err) {
      return rejectWithValue(err)
    }
  }
)

export const initialUserPreferenceState = _initializeState()

export const UserpreferenceSlice = createSlice({
  name: sliceName,
  initialState: initialUserPreferenceState,
  reducers: {
    updatePreferences(state, action: PayloadAction<LocalStorageUser | null>) {
      //User is logged out
      if(action.payload === null) {
        state.userId = null
        state.data = {}
        return
      }

      state.userId = parseInt(action.payload.id)
      
      state.data = Object.entries(action.payload.preferences)
      .reduce<UserPreferenceStateData>((a, [name, i]) => {
        a[name] = _formatter({id: i.id, val: `${i.val}`, name})
        return a
      }, {})
    } 
  },
  extraReducers(builder) {
    builder
    .addCase(getAllUserPreference.fulfilled, (state, action) => {
      state.data = action.payload
      state.error = null,
      state.loading = false
    })
    .addCase(getAllUserPreference.pending, (state, _action) => {      
      state.error = null,
      state.loading = true
    })
    .addCase(getAllUserPreference.rejected, (state, action) => {
      state.error = formatError(action.payload),
      state.loading = false
    })
    .addCase(updateUserPreference.fulfilled, (state, action) => {
      state.data[action.payload.name] = action.payload
      state.error = null,
      state.loading = false
    })
    .addCase(updateUserPreference.pending, (state, action) => {
      if(!state.data[action.meta.arg.name]) {
        state.data[action.meta.arg.name] = {
          name: action.meta.arg.name,
          loading: false,
          error: null,
          val: action.meta.arg.val,
          id: 0
        }
      }
      
      state.data[action.meta.arg.name]!.loading = true
      state.data[action.meta.arg.name]!.error = null
    })
    .addCase(updateUserPreference.rejected, (state, action) => {
      if(!state.data[action.meta.arg.name]) {
        state.data[action.meta.arg.name] = {
          name: action.meta.arg.name,
          loading: false,
          error: null,
          val: action.meta.arg.val,
          id: 0
        }
      }

      state.data[action.meta.arg.name]!.loading = false
      state.data[action.meta.arg.name]!.error = formatError(action.payload).message
    })
  },
})
